import { useEffect, useId, useRef, useState } from 'react'

import type { EntityType } from '@/api/actions'

import { TYPE_LABEL } from '../describe'
import { TYPE_ICON } from '../parts'
import { useEditor, useIssues } from './context'
import { asObj, asStrings, isEmpty, setOrDelete, type Obj } from './doc'
import { AddButton, ChipPicker, ComboInput, Disclosure, EditField, EditGroup, ListControls, nameNormalize, TagInput, ValueInput } from './fields'

/**
 * What may fill a participant (§11.2 `RoleSpec`): which kinds of things, what they must be able to
 * do, which body parts they need — and, folded away, puppet kinds, body shapes and the state they
 * must be in. Used for an action's roles, a world rule's two sides and a "participant must be…"
 * check, so `path` is wherever the spec lives in the document.
 */

const words = (s: string): string => s.replace(/_/g, ' ')

const SOCKET_LABEL: Record<string, string> = {
  hand: 'A hand',
  other_hand: 'A second hand',
  mouth: 'A mouth',
  head: 'A head',
  back: 'A back',
  top: 'A top',
}

export function RoleSpecFields({ path, spec, onChange, who }: { path: string; spec: Obj; onChange(next: Obj): void; who: string }) {
  const { meta, meanings, lists } = useEditor()
  const set = (key: string, value: unknown): void => onChange(setOrDelete(spec, [key], value))
  // The folded part opens by itself when a problem or a hint is inside it.
  const issues = useIssues(path, true)
  const more = [...issues.errors, ...issues.warnings].some((e) => /\.(kinds_any|plans_any|state)(\.|$)/.test(e.path))
  const hasMore = !isEmpty(spec.kinds_any) || !isEmpty(spec.plans_any) || !isEmpty(spec.state)
  const typeOptions = meta.entity_types.map((t) => {
    const Icon = TYPE_ICON[t as EntityType]
    return { value: t, label: TYPE_LABEL[t as EntityType] ?? words(t), icon: Icon ? <Icon className="size-3.5" aria-hidden /> : undefined }
  })
  const capTitle = (c: string): string | undefined => meanings[c]
  return (
    <div className="flex flex-col gap-3">
      <EditGroup path={`${path}.types`} label={`What can ${who} be?`} hint="Leave them all off for anything.">
        <ChipPicker label={`What can ${who} be?`} values={asStrings(spec.types)} options={typeOptions} onChange={(v) => set('types', v)} />
      </EditGroup>
      <div className="grid gap-3 md:grid-cols-2">
        <EditField path={`${path}.capabilities_all`} label="Must be able to (all of these)" hint="For example: throwable, can_swim.">
          {(ctrl) => <TagInput ctrl={ctrl} values={asStrings(spec.capabilities_all)} onChange={(v) => set('capabilities_all', v)} suggestions={meta.capabilities} listId={lists?.capabilities} labelOf={words} titleOf={capTitle} placeholder="Add an ability…" />}
        </EditField>
        <EditField path={`${path}.capabilities_any`} label="…or at least one of these">
          {(ctrl) => <TagInput ctrl={ctrl} values={asStrings(spec.capabilities_any)} onChange={(v) => set('capabilities_any', v)} suggestions={meta.capabilities} listId={lists?.capabilities} labelOf={words} titleOf={capTitle} placeholder="Add an ability…" />}
        </EditField>
      </div>
      <EditGroup path={`${path}.needs`} label="Needs these body parts">
        <ChipPicker label="Needs these body parts" values={asStrings(spec.needs)} options={meta.sockets.map((s) => ({ value: s, label: SOCKET_LABEL[s] ?? words(s) }))} onChange={(v) => set('needs', v)} />
      </EditGroup>
      <Disclosure label="More: kinds, body shapes, what it must be doing" open={hasMore || more}>
        <div className="grid gap-3 md:grid-cols-2">
          <EditField path={`${path}.kinds_any`} label="Only these kinds" hint="person, animal, bird…">
            {(ctrl) => <TagInput ctrl={ctrl} values={asStrings(spec.kinds_any)} onChange={(v) => set('kinds_any', v)} suggestions={meta.kinds} placeholder="Any kind" />}
          </EditField>
          <EditField path={`${path}.plans_any`} label="Only these body shapes" hint="humanoid, quadruped, bird…">
            {(ctrl) => <TagInput ctrl={ctrl} values={asStrings(spec.plans_any)} onChange={(v) => set('plans_any', v)} suggestions={meta.plans} placeholder="Any shape" />}
          </EditField>
        </div>
        <KeyValueRows
          path={`${path}.state`}
          label={`What ${who} must be doing or be now`}
          hint="For example: holder = $thrower (someone holds it), wet = true."
          value={asObj(spec.state)}
          onChange={(v) => set('state', v)}
        />
      </Disclosure>
    </div>
  )
}

interface Row {
  key: string
  value: unknown
}

const toRows = (o: Obj): Row[] => Object.entries(o).map(([key, value]) => ({ key, value }))
const toObj = (rows: Row[]): Obj => Object.fromEntries(rows.filter((r) => r.key !== '').map((r) => [r.key, r.value]))

/**
 * A small `{key: value}` table (a role's required state, a rule's state). Rows are kept locally so
 * a half-typed key does not jump around; the document gets every row that has a key.
 */
export function KeyValueRows({ path, label, hint, value, onChange }: { path: string; label: string; hint?: string; value: Obj; onChange(next: Obj): void }) {
  const { meta, lists } = useEditor()
  const [rows, setRows] = useState<Row[]>(() => toRows(value))
  const sent = useRef(JSON.stringify(value))
  useEffect(() => {
    const now = JSON.stringify(value)
    if (now !== sent.current) {
      sent.current = now
      setRows(toRows(value))
    }
  }, [value])
  const commit = (next: Row[]): void => {
    setRows(next)
    const obj = toObj(next)
    sent.current = JSON.stringify(obj)
    onChange(obj)
  }
  return (
    <EditGroup path={path} label={label} hint={hint} deep={false}>
      {rows.length > 0 ? (
        <ul className="flex flex-col gap-2">
          {rows.map((r, i) => (
            <KeyValueRow
              key={i}
              path={path}
              row={r}
              index={i}
              suggestions={meta.state_keys}
              listId={lists?.stateKeys}
              onChange={(next) => commit(rows.map((x, j) => (j === i ? next : x)))}
              onRemove={() => commit(rows.filter((_, j) => j !== i))}
            />
          ))}
        </ul>
      ) : null}
      <AddButton onClick={() => setRows([...rows, { key: '', value: true }])}>Add a condition</AddButton>
    </EditGroup>
  )
}

function KeyValueRow({ path, row, index, suggestions, listId, onChange, onRemove }: { path: string; row: Row; index: number; suggestions: string[]; listId?: string; onChange(next: Row): void; onRemove(): void }) {
  const keyId = useId()
  const name = row.key ? row.key.replace(/_/g, ' ') : `condition ${index + 1}`
  return (
    <li className="grid grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] items-start gap-2">
      <div className="flex min-w-0 flex-col">
        <label htmlFor={keyId} className="sr-only">
          What (condition {index + 1})
        </label>
        <ComboInput
          ctrl={{ id: keyId, invalid: false, describedBy: undefined }}
          value={row.key}
          suggestions={suggestions}
          listId={listId}
          normalize={nameNormalize}
          placeholder="holder, wet, mood…"
          onChange={(k) => onChange({ ...row, key: k ?? '' })}
        />
      </div>
      <EditField path={row.key ? `${path}.${row.key}` : path} label={`Value of ${name}`} hideLabel deep>
        {(ctrl) => <ValueInput ctrl={ctrl} value={row.value} stateKey={row.key} onChange={(v) => onChange({ ...row, value: v })} />}
      </EditField>
      <ListControls name={name} index={index} count={1} onRemove={onRemove} />
    </li>
  )
}
